// In-memory cache of per-day globe points, so scrubbing the timeline can
// redraw from memory instead of waiting on the network.
//
// Two tiers per day:
//   - compact: positions/importance/category/country only, fetched a month at
//     a time from /timeline — enough to draw the globe.
//   - full: the complete article payload from /articles_date (titles, links,
//     images…), fetched once the thumb settles, needed for popups and lists.

const MONTH_RETRY_MS = 30_000;
const MAX_COMPACT_DAYS = 90;
const MAX_FULL_DAYS = 30;

export const monthOf = (dayKey) => dayKey.slice(0, 7);

export const HOUR_MS = 3_600_000;
export const WINDOW_MS = 24 * HOUR_MS;

// "YYYY-MM-DD" of the UTC day containing `ms`.
export const utcDayKey = (ms) => new Date(ms).toISOString().slice(0, 10);

// The UTC days a rolling window [endMs − 24h, endMs) overlaps (one or two).
export function windowDays(endMs) {
  return [...new Set([utcDayKey(endMs - WINDOW_MS), utcDayKey(endMs - 1)])];
}

function setLru(map, key, value, max) {
  map.delete(key);
  map.set(key, value);
  if (map.size > max) map.delete(map.keys().next().value);
}

// events: the month's [eventId, storylineId, startMinute, sourceCount, title]
// rows, referenced by each row's last column (-1 or absent: ungrouped).
export function compactRowsToPoints(rows, categories, countries, events = []) {
  return rows.map(([id, lat, lng, importance, catIdx, countryIdx, minute, eventIdx = -1]) => {
    const event = eventIdx >= 0 ? events[eventIdx] : undefined;
    return {
      article_id: id,
      lat,
      lng,
      val: importance || 5,
      category: catIdx >= 0 ? categories[catIdx] : undefined,
      country: countryIdx >= 0 ? countries[countryIdx] : undefined,
      publishedAt: minute * 60_000,
      eventId: event?.[0],
      storylineId: event?.[1] || undefined,
    };
  });
}

export function createTimelineCache({ fetchMonth, maxConcurrent = 3 }) {
  const months = new Map();        // "YYYY-MM" → /timeline payload
  const failedAt = new Map();      // "YYYY-MM" → timestamp of last failure
  const inFlight = new Set();
  let queue = [];                  // months waiting to be fetched, most urgent first
  const compactDays = new Map();   // dayKey → points (LRU of converted days)
  const fullDays = new Map();      // dayKey → points (LRU)
  const listeners = new Set();
  const eventMeta = new Map();       // eventId → { storylineId, start, sources, title }
  const storylineEvents = new Map(); // storylineId → Map(eventId → { eventId, start, sources, title })

  const indexEvents = (rows = []) => {
    for (const [eventId, storylineId, startMinute, sources, title] of rows) {
      const meta = { storylineId: storylineId || undefined, start: startMinute != null ? startMinute * 60_000 : undefined, sources, title };
      eventMeta.set(eventId, meta);
      if (!storylineId) continue;
      if (!storylineEvents.has(storylineId)) storylineEvents.set(storylineId, new Map());
      storylineEvents.get(storylineId).set(eventId, { eventId, start: meta.start, sources, title });
    }
  };
  let lastWindow = null;           // memo so an unchanged window keeps its identity

  const notify = (dayKeys) => listeners.forEach((fn) => fn(dayKeys));

  const pump = () => {
    while (inFlight.size < maxConcurrent && queue.length) {
      const month = queue.shift();
      inFlight.add(month);
      fetchMonth(month)
        .then((payload) => {
          months.set(month, payload);
          indexEvents(payload.events);
          failedAt.delete(month);
          notify(new Set(Object.keys(payload.days || {}).concat(`${month}-*`)));
        })
        .catch(() => failedAt.set(month, Date.now()))
        .finally(() => {
          inFlight.delete(month);
          pump();
        });
    }
  };

  return {
    // Returns { points, full } for a day, or undefined if nothing is loaded yet.
    getDay(dayKey) {
      const full = fullDays.get(dayKey);
      if (full) return { points: full, full: true };
      const month = months.get(monthOf(dayKey));
      if (!month) return undefined;
      let points = compactDays.get(dayKey);
      if (!points) {
        points = compactRowsToPoints(month.days?.[dayKey] || [], month.categories || [], month.countries || [], month.events || []);
        setLru(compactDays, dayKey, points, MAX_COMPACT_DAYS);
      }
      return { points, full: false };
    },

    // Points published in the rolling window [endMs − 24h, endMs), stitched
    // from the one or two UTC days it overlaps. Undefined until every one of
    // those days is loaded; `full` only when all of them have full payloads.
    getWindow(endMs) {
      const days = windowDays(endMs).map((key) => this.getDay(key));
      if (days.some((d) => !d)) return undefined;
      const sources = days.map((d) => d.points);
      if (lastWindow && lastWindow.endMs === endMs &&
          lastWindow.sources.length === sources.length &&
          lastWindow.sources.every((src, i) => src === sources[i])) {
        return lastWindow.entry;
      }
      const start = endMs - WINDOW_MS;
      const points = sources.flat().filter((p) => p.publishedAt >= start && p.publishedAt < endMs);
      const full = days.every((d) => d.full);
      // Sliding by a minute usually doesn't change which articles are in the
      // window; keep the previous result then so nothing downstream rebuilds.
      const prev = lastWindow?.entry;
      const same = prev && prev.full === full && prev.points.length === points.length &&
        prev.points.every((p, i) => p === points[i]);
      const entry = same ? prev : { points, full };
      lastWindow = { endMs, sources, entry };
      return entry;
    },

    setFullDay(dayKey, points) {
      setLru(fullDays, dayKey, points, MAX_FULL_DAYS);
      notify(new Set([dayKey]));
    },

    // Queue months for fetching; earlier entries are fetched first and jump
    // ahead of anything already waiting.
    requestMonths(wanted) {
      const now = Date.now();
      const fresh = wanted.filter((m) =>
        !months.has(m) && !inFlight.has(m) && !(now - (failedAt.get(m) || 0) < MONTH_RETRY_MS)
      );
      queue = [...new Set([...fresh, ...queue])];
      pump();
    },

    getEventMeta(eventId) {
      return eventMeta.get(eventId);
    },

    getStorylineEvents(storylineId) {
      return [...(storylineEvents.get(storylineId)?.values() || [])];
    },

    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}
